<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  fetchValuationBand,
  computeReverseDcf,
  type ValuationBandReport,
  type ReverseDcf
} from '@/api/client'

const props = defineProps<{
  /** 报告页挂载时透传当前标的 (独立使用时缺省 sh.600519) */
  initialSymbol?: string
}>()

const symbol = ref(props.initialSymbol || 'sh.600519')
const years = ref(3)
const loading = ref(false)
const error = ref<string | null>(null)
const report = ref<ValuationBandReport | null>(null)

const dcfForm = ref({ market_cap: 2400, fcf_ttm: 800, wacc: 0.09, years: 10, terminal_growth: 0.025 })
const dcfLoading = ref(false)
const dcfResult = ref<ReverseDcf | null>(null)

const peRows = computed(() => {
  if (!report.value) return []
  const r = report.value
  return [
    { label: '20% 分位', quantile: r.pe_quantiles.q20, band: r.pe_price_bands.q20 },
    { label: '40% 分位', quantile: r.pe_quantiles.q40, band: r.pe_price_bands.q40 },
    { label: '60% 分位', quantile: r.pe_quantiles.q60, band: r.pe_price_bands.q60 },
    { label: '80% 分位', quantile: r.pe_quantiles.q80, band: r.pe_price_bands.q80 }
  ]
})

const pbRows = computed(() => {
  if (!report.value) return []
  const r = report.value
  return [
    { label: '20% 分位', quantile: r.pb_quantiles.q20, band: r.pb_price_bands.q20 },
    { label: '40% 分位', quantile: r.pb_quantiles.q40, band: r.pb_price_bands.q40 },
    { label: '60% 分位', quantile: r.pb_quantiles.q60, band: r.pb_price_bands.q60 },
    { label: '80% 分位', quantile: r.pb_quantiles.q80, band: r.pb_price_bands.q80 }
  ]
})

const comparisons = computed(() => dcfResult.value?.comparisons || null)

function pctColor(p: number | null): string {
  if (p == null) return 'info'
  if (p <= 25) return 'success'
  if (p <= 50) return 'info'
  if (p <= 75) return 'warning'
  return 'danger'
}

async function load() {
  if (!symbol.value.trim()) return
  loading.value = true
  error.value = null
  try {
    report.value = await fetchValuationBand(symbol.value.trim(), years.value)
    if (report.value.reverse_dcf?.available) {
      dcfResult.value = report.value.reverse_dcf
      const mc = report.value.reverse_dcf.market_cap
      const fcf = report.value.reverse_dcf.fcf_ttm
      dcfForm.value.market_cap = mc > 1e6 ? Number((mc / 1e8).toFixed(2)) : mc
      dcfForm.value.fcf_ttm = fcf > 1e6 ? Number((fcf / 1e8).toFixed(2)) : fcf
    }
  } catch (e: any) {
    error.value = e?.response?.data?.detail || e?.message || String(e)
  } finally {
    loading.value = false
  }
}

async function runDcf() {
  dcfLoading.value = true
  try {
    const mc = dcfForm.value.market_cap
    const fcf = dcfForm.value.fcf_ttm
    dcfResult.value = await computeReverseDcf({
      ...dcfForm.value,
      market_cap: mc > 1e6 ? mc : mc * 1e8,
      fcf_ttm: fcf > 1e6 ? fcf : fcf * 1e8,
      symbol: symbol.value.trim() || undefined,
    })
  } catch (e: any) {
    error.value = e?.response?.data?.detail || e?.message || String(e)
  } finally {
    dcfLoading.value = false
  }
}

// 报告页挂载 (initialSymbol 透传) 时自动加载一次
onMounted(() => {
  if (props.initialSymbol) load()
})
</script>

<template>
  <div class="valuation-panel">
    <el-card shadow="never">
      <template #header>
        <div class="header-bar">
          <span class="title">📐 历史估值通道与交互式逆向 DCF (ALENS-122/194)</span>
          <div class="controls">
            <el-input v-model="symbol" placeholder="sh.600519" style="width: 130px;" size="small" />
            <el-radio-group v-model="years" size="small" @change="load">
              <el-radio-button :value="1">1年</el-radio-button>
              <el-radio-button :value="3">3年</el-radio-button>
              <el-radio-button :value="5">5年</el-radio-button>
              <el-radio-button :value="10">10年</el-radio-button>
            </el-radio-group>
            <el-button size="small" type="primary" :loading="loading" @click="load">查询</el-button>
          </div>
        </div>
      </template>

      <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" />
      <el-alert
        v-for="w in report?.warnings ?? []"
        :key="w"
        :title="w"
        type="info"
        :closable="false"
        class="warn"
      />

      <el-row v-if="report" :gutter="16">
        <el-col :span="10">
          <div class="sub-title">PE/PB 历史分位</div>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="PE (TTM)">
              <el-tag :type="pctColor(report.pe_percentile)" size="small">
                {{ report.pe_now?.toFixed(1) ?? '-' }} · {{ report.pe_percentile ?? '-' }}% 分位
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="PB">
              <el-tag :type="pctColor(report.pb_percentile)" size="small">
                {{ report.pb_now?.toFixed(2) ?? '-' }} · {{ report.pb_percentile ?? '-' }}% 分位
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>

          <div class="sub-title" style="margin-top: 10px;">PE-Band 分位线 → 价格通道</div>
          <el-table :data="peRows" size="small" border>
            <el-table-column prop="label" label="分位" width="90" />
            <el-table-column prop="quantile" label="PE" align="right" />
            <el-table-column label="价格带" align="right">
              <template #default="{ row }">{{ row.band != null ? row.band.toFixed(1) : '-' }}</template>
            </el-table-column>
          </el-table>

          <div class="sub-title" style="margin-top: 10px;">PB-Band 分位线 → 价格通道</div>
          <el-table :data="pbRows" size="small" border>
            <el-table-column prop="label" label="分位" width="90" />
            <el-table-column prop="quantile" label="PB" align="right" />
            <el-table-column label="价格带" align="right">
              <template #default="{ row }">{{ row.band != null ? row.band.toFixed(1) : '-' }}</template>
            </el-table-column>
          </el-table>
        </el-col>

        <el-col :span="14">
          <div class="sub-title">交互式逆向 DCF: 敏感度调节反推隐含复合增长率</div>
          <el-form :inline="true" size="small">
            <el-form-item label="市值(亿)">
              <el-input-number v-model="dcfForm.market_cap" :step="10" :min="0.1" :precision="2" />
            </el-form-item>
            <el-form-item label="FCF(亿)">
              <el-input-number v-model="dcfForm.fcf_ttm" :step="1" :min="0.01" :precision="2" />
            </el-form-item>
            <el-form-item label="年限">
              <el-input-number v-model="dcfForm.years" :min="5" :max="10" @change="runDcf" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :loading="dcfLoading" @click="runDcf">反推</el-button>
            </el-form-item>
          </el-form>

          <!-- WACC / 永续增速敏感度滑块 (ALENS-194) -->
          <div class="slider-block">
            <div class="slider-row">
              <span class="slider-label">WACC</span>
              <el-slider
                v-model="dcfForm.wacc"
                :min="0.08"
                :max="0.14"
                :step="0.005"
                :format-tooltip="(v: number) => (v * 100).toFixed(1) + '%'"
                class="slider"
                @change="runDcf"
              />
              <span class="slider-value">{{ (dcfForm.wacc * 100).toFixed(1) }}%</span>
            </div>
            <div class="slider-row">
              <span class="slider-label">永续增速</span>
              <el-slider
                v-model="dcfForm.terminal_growth"
                :min="0.015"
                :max="0.03"
                :step="0.001"
                :format-tooltip="(v: number) => (v * 100).toFixed(1) + '%'"
                class="slider"
                @change="runDcf"
              />
              <span class="slider-value">{{ (dcfForm.terminal_growth * 100).toFixed(1) }}%</span>
            </div>
          </div>

          <el-descriptions v-if="dcfResult" :column="2" border size="small">
            <el-descriptions-item label="隐含增长率" :span="2">
              <span :style="{ color: (dcfResult.implied_growth_pct ?? 0) < 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }">
                {{ dcfResult.implied_growth_pct != null ? dcfResult.implied_growth_pct + '%' : '无解' }}
              </span>
              <span class="dcf-params"> · WACC {{ (dcfResult.wacc * 100).toFixed(1) }}% / 永续 {{ (dcfResult.terminal_growth * 100).toFixed(1) }}% / {{ dcfResult.years }} 年</span>
            </el-descriptions-item>
            <el-descriptions-item label="零增长公允价值">
              {{ dcfResult.fair_value_at_zero_growth != null ? (dcfResult.fair_value_at_zero_growth / 1e8).toFixed(0) + ' 亿' : '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="解读">
              {{ dcfResult.verdict ?? '-' }}
            </el-descriptions-item>
          </el-descriptions>

          <!-- 增速对比: 隐含 vs 营收 CAGR vs 行业均值 (ALENS-194) -->
          <div v-if="comparisons" class="comparison-block">
            <div class="sub-title" style="margin-top: 10px;">隐含增速对比基准</div>
            <el-table
              :data="[{
                implied: comparisons.implied_growth_pct,
                revenue: comparisons.revenue_cagr_3y_pct,
                industry: comparisons.industry_avg_growth_pct
              }]"
              size="small"
              border
            >
              <el-table-column label="隐含增速" align="center">
                <template #default="{ row }">{{ row.implied != null ? row.implied + '%' : '-' }}</template>
              </el-table-column>
              <el-table-column label="近 3 年营收 CAGR" align="center">
                <template #default="{ row }">{{ row.revenue != null ? row.revenue + '%' : '-' }}</template>
              </el-table-column>
              <el-table-column label="行业平均增速" align="center">
                <template #default="{ row }">{{ row.industry != null ? row.industry + '%' : '-' }}</template>
              </el-table-column>
            </el-table>
            <div v-if="comparisons.note_vs_revenue" class="comparison-note">
              {{ comparisons.note_vs_revenue }}
            </div>
          </div>

          <div class="sub-title" style="margin-top: 10px;">估值锚定网格挂单</div>
          <el-table v-if="report.grid.length" :data="report.grid" size="small" border>
            <el-table-column prop="price" label="价格" width="90" align="right" />
            <el-table-column prop="anchor" label="估值锚" width="80" />
            <el-table-column label="目标仓位" width="90" align="right">
              <template #default="{ row }">{{ (row.weight * 100).toFixed(0) }}%</template>
            </el-table-column>
            <el-table-column prop="note" label="说明" />
          </el-table>
          <el-empty v-else description="价格带数据不足, 网格不可用" :image-size="48" />
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<style scoped>
.valuation-panel { display: flex; flex-direction: column; gap: 12px; }
.header-bar { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.controls { display: flex; gap: 8px; }
.title { font-size: 15px; font-weight: 600; }
.sub-title { font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }
.warn { margin-bottom: 8px; }
.slider-block {
  border: 1px solid var(--border-color); border-radius: 8px;
  padding: 8px 14px; margin-bottom: 10px; background: var(--bg-elevated);
}
.slider-row { display: flex; align-items: center; gap: 12px; }
.slider-label { font-size: 12px; color: var(--text-secondary); width: 52px; flex-shrink: 0; }
.slider { flex: 1; }
.slider-value { font-size: 12px; font-weight: 700; color: var(--text-primary); width: 44px; text-align: right; flex-shrink: 0; }
.dcf-params { font-size: 12px; color: var(--text-secondary); font-weight: normal; }
.comparison-note {
  font-size: 12px; color: var(--text-secondary); margin-top: 6px; line-height: 1.5;
}
</style>
